import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  List,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { DndContext, DragEndEvent, SensorDescriptor, SensorOptions, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from './drag';
import LinkRow from './LinkRow';
import LinkForm, { LinkFormValues } from './LinkForm';
import SectionForm, { SectionFormValues } from './SectionForm';
import { LinkItem, LinkSection, getSectionIcon } from './types';

interface SectionProps {
  section: LinkSection;
  sections: LinkSection[];
  isOpen: boolean;
  editMode: boolean;
  sensors: SensorDescriptor<SensorOptions>[];
  onToggle: (id: string) => void;
  onAddLink: (sectionId: string, values: LinkFormValues) => void;
  onUpdateLink: (sectionId: string, linkId: string, values: LinkFormValues) => void;
  onDeleteLink: (sectionId: string, link: LinkItem) => void;
  onReorderLinks: (sectionId: string, activeId: string, overId: string) => void;
  onUpdateSection: (sectionId: string, values: SectionFormValues) => void;
  onDeleteSection: (section: LinkSection) => void;
}

const Section = ({
  section,
  sections,
  isOpen,
  editMode,
  sensors,
  onToggle,
  onAddLink,
  onUpdateLink,
  onDeleteLink,
  onReorderLinks,
  onUpdateSection,
  onDeleteSection,
}: SectionProps) => {
  const [editingLinkId, setEditingLinkId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState(false);

  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: section.id, disabled: !editMode });

  // Leaving edit mode drops any half-finished forms.
  useEffect(() => {
    if (!editMode) {
      setEditingLinkId(null);
      setAdding(false);
      setRenaming(false);
    }
  }, [editMode]);

  const Icon = getSectionIcon(section.icon);

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id) {
      onReorderLinks(section.id, String(active.id), String(over.id));
    }
  };

  return (
    <Box
      ref={setNodeRef}
      sx={{
        px: 1,
        py: 1.5,
        borderRadius: 2,
        transform: CSS.Transform.toString(transform),
        transition,
        position: 'relative',
        zIndex: isDragging ? 2 : 'auto',
        opacity: isDragging ? 0.7 : 1,
        bgcolor: isDragging ? 'background.paper' : 'transparent',
      }}
    >
      {renaming ? (
        <SectionForm
          initial={{ title: section.title, icon: section.icon }}
          submitLabel="Save"
          onCancel={() => setRenaming(false)}
          onSubmit={(values) => {
            onUpdateSection(section.id, values);
            setRenaming(false);
          }}
        />
      ) : (
        <Box
          onClick={() => onToggle(section.id)}
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            transition: 'transform 0.25s cubic-bezier(0.2, 0, 0, 1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: -4,
              borderRadius: 8,
              backgroundColor: 'action.hover',
              opacity: 0,
              transition: 'opacity 0.3s cubic-bezier(0.2, 0, 0, 1)',
              pointerEvents: 'none',
            },
            '&:hover::before': { opacity: 1 },
            '&:active': { transform: 'scale(0.985)' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            {editMode && (
              <IconButton
                ref={setActivatorNodeRef}
                size="small"
                aria-label={`Drag ${section.title} section`}
                onClick={(e) => e.stopPropagation()}
                sx={{ cursor: 'grab', touchAction: 'none', color: 'text.secondary', ml: -0.75, mr: -0.5 }}
                {...attributes}
                {...listeners}
              >
                <DragIndicatorIcon fontSize="small" />
              </IconButton>
            )}
            <Icon sx={{ fontSize: 20 }} color="secondary" />
            <Typography variant="h6" color="primary" noWrap sx={{ textTransform: 'capitalize' }}>
              {section.title}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            {editMode && (
              <>
                <Tooltip title="Rename section">
                  <IconButton
                    size="small"
                    aria-label={`Rename ${section.title} section`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setRenaming(true);
                    }}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete section">
                  <IconButton
                    size="small"
                    aria-label={`Delete ${section.title} section`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteSection(section);
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <IconButton
              size="small"
              color="primary"
              aria-label={isOpen ? 'Collapse' : 'Expand'}
              sx={{
                transition: 'transform 0.35s cubic-bezier(0.2, 0, 0, 1)',
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          </Box>
        </Box>
      )}

      <Collapse
        in={isOpen}
        timeout={320}
        easing={{ enter: 'cubic-bezier(0.2, 0, 0, 1)', exit: 'cubic-bezier(0.2, 0, 0, 1)' }}
      >
        <List disablePadding sx={{ mt: 1 }}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={section.links.map((link) => link.id)} strategy={verticalListSortingStrategy}>
              {section.links.map((link) =>
                editingLinkId === link.id ? (
                  <LinkForm
                    key={link.id}
                    initial={{ label: link.label, to: link.to, sectionId: section.id }}
                    sections={sections}
                    submitLabel="Save"
                    onCancel={() => setEditingLinkId(null)}
                    onSubmit={(values) => {
                      onUpdateLink(section.id, link.id, values);
                      setEditingLinkId(null);
                    }}
                  />
                ) : (
                  <LinkRow
                    key={link.id}
                    link={link}
                    editMode={editMode}
                    onEdit={(item) => setEditingLinkId(item.id)}
                    onDelete={(item) => onDeleteLink(section.id, item)}
                  />
                )
              )}
            </SortableContext>
          </DndContext>

          {editMode && section.links.length === 0 && !adding && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 0.5 }}>
              No links yet.
            </Typography>
          )}

          {editMode &&
            (adding ? (
              <LinkForm
                initial={{ label: '', to: '', sectionId: section.id }}
                submitLabel="Add"
                onCancel={() => setAdding(false)}
                onSubmit={(values) => {
                  onAddLink(section.id, values);
                  setAdding(false);
                }}
              />
            ) : (
              <Button
                size="small"
                color="secondary"
                startIcon={<AddIcon />}
                onClick={() => setAdding(true)}
                sx={{ mt: 0.5, ml: 0.5 }}
              >
                Add link
              </Button>
            ))}
        </List>
      </Collapse>
    </Box>
  );
};

export default Section;
